import React, { useEffect, useRef, useState } from "react"; // ✨ useEffect 하나만 사용합니다.
import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import SelectCheckbox from "../../components/onboarding/SelectCheckBox";
import SignatureCanvas from "react-signature-canvas";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import Typography from "@mui/joy/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../api";
import {
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "../../api/paymentApi";
import { refundApi } from "../../api/refundApi";
import CustomModal from "components/common/CustomModal";
import useClose from "../../hooks/useClose";
import { paymentOrderAtom } from "../../recoil/paymentOrderAtom";

// 옵션 리스트
const options: Option[] = [
  { id: 1, text: "환불일로부터 30일 동안 프로그램을 다시 시작할 수 없습니다." },
  {
    id: 2,
    text: "30일 이후 프로그램을 다시 시작한다면, 환불 보장제가 적용되지 않습니다.",
  },
  {
    id: 3,
    text: "환불 고객의 데이터는 의무 보존 대상이 아니므로, 프로그램을 다시 시작하게 된다면 처음부터 시작해야 할 수 있습니다.",
  },
];

interface Option {
  id: number;
  text: string;
}

export default function RefundRequest() {
  const user = useRecoilValue(userAtom);
  const paymentOrderState = useRecoilValue(paymentOrderAtom);

  const [multiSelectionIdList, setMultiSelectionIdList] = useState<number[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const ns = `refund_request_1:${user?.userId ?? "anon"}`;
      const currentKey = `${ns}:checks`;
      const anonKey = `refund_request_1:anon:checks`;
      const raw = sessionStorage.getItem(currentKey) ?? sessionStorage.getItem(anonKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const ns = `refund_request_1:${user?.userId ?? "anon"}`;
      const currentKey = `${ns}:signature`;
      const anonKey = `refund_request_1:anon:signature`;
      return sessionStorage.getItem(currentKey) ?? sessionStorage.getItem(anonKey);
    } catch {
      return null;
    }
  });
  const [isSigned, setIsSigned] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const ns = `refund_request_1:${user?.userId ?? "anon"}`;
      const currentKey = `${ns}:signature`;
      const anonKey = `refund_request_1:anon:signature`;
      const saved = sessionStorage.getItem(currentKey) ?? sessionStorage.getItem(anonKey);
      return !!saved;
    } catch {
      return false;
    }
  });
  const [isRequestRefundLoading, setIsRequestRefundLoading] = useState(false);
  const [canvasKey, setCanvasKey] = useState(Date.now());

  const sigRef = useRef<any>(null);
  const signatureContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [pixelRatio, setPixelRatio] = useState(1);
  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const close = useClose();

  const { data: paymentOrderDetailData } = useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT, user?.userId],
    () => paymentApi.getOrderDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
    }
  );

  // sessionStorage 키 네임스페이스 (사용자별 분리)
  const STORAGE_PREFIX = "refund_request_1";
  const storageNS = `${STORAGE_PREFIX}:${user?.userId ?? "anon"}`;
  const CHECKS_KEY = `${storageNS}:checks`;
  const SIGNATURE_KEY = `${storageNS}:signature`;
  const VISIBLE_COUNT_KEY = `${storageNS}:visibleCount`;
  // anon 네임스페이스(사용자 정보 로딩 전) 키
  const ANON_NS = `${STORAGE_PREFIX}:anon`;
  const ANON_CHECKS_KEY = `${ANON_NS}:checks`;
  const ANON_SIGNATURE_KEY = `${ANON_NS}:signature`;
  const ANON_VISIBLE_COUNT_KEY = `${ANON_NS}:visibleCount`;
  // 서명 복원은 캔버스 준비 후 단 한 번만
  const restoredRef = useRef(false);

  // 진행 단계 상태: 세션 저장값 또는 체크 상태 기반(한번 열린 단계는 닫히지 않음)
  const [visibleOptionsCount, setVisibleOptionsCount] = useState<number>(() => {
    const maxDisplay = options.length + 1;
    try {
      // 1) 세션에서 우선 복원
      const saved = sessionStorage.getItem(VISIBLE_COUNT_KEY);
      if (saved) {
        const n = parseInt(saved, 10);
        if (!Number.isNaN(n)) {
          return Math.min(maxDisplay, Math.max(1, n));
        }
      }
      // 2) fallback: 체크 상태 기반
      const savedChecksRaw = sessionStorage.getItem(CHECKS_KEY);
      const list = savedChecksRaw ? (JSON.parse(savedChecksRaw) as number[]) : [];
      const maxChecked = Array.isArray(list) && list.length > 0 ? Math.max(...list) : 0;
      let initial = Math.min(maxDisplay, Math.max(1, maxChecked + 1));
      // 3) 저장된 서명이 있으면 서명 단계(4)까지는 항상 보이게
      const savedSig = sessionStorage.getItem(SIGNATURE_KEY);
      if (savedSig) {
        initial = Math.max(initial, 4);
      }
      return initial;
    } catch {
      return 1;
    }
  });
  const showSignature = visibleOptionsCount >= 4;

  useEffect(() => {
    if (!showSignature) return;
    if (signatureContainerRef.current) {
      const { width, height } =
        signatureContainerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setCanvasSize({ width, height });
      }
    }
  }, [showSignature]); // 서명 영역 표시 시 계산

  // Ensure high-DPI clarity for the signature canvas
  useEffect(() => {
    if (!sigRef.current) return;
    const canvas: HTMLCanvasElement = sigRef.current.getCanvas();
    // Set the canvas's actual pixel size to account for device pixel ratio
    canvas.width = Math.max(1, Math.floor(canvasSize.width * pixelRatio));
    canvas.height = Math.max(1, Math.floor(canvasSize.height * pixelRatio));
    // Keep visual size the same via CSS
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Reset any existing transforms, then scale for crisp strokes
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(pixelRatio, pixelRatio);
  }, [canvasSize.width, canvasSize.height, pixelRatio, canvasKey]);

  useEffect(() => {
    // Update pixel ratio on mount; in case of zoom or DPR changes, you may extend this.
    if (typeof window !== "undefined") {
      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      setPixelRatio(dpr);
    }
  }, []);

  // 사용자 정보 로딩 후 anon 네임스페이스의 저장값을 사용자 네임스페이스로 마이그레이션
  useEffect(() => {
    if (typeof window === "undefined") return;
    // 아직 사용자 ID가 없으면 대기
    if (storageNS.endsWith(":anon")) return;

    try {
      const anonChecks = sessionStorage.getItem(ANON_CHECKS_KEY);
      const anonSig = sessionStorage.getItem(ANON_SIGNATURE_KEY);
      const anonVisible = sessionStorage.getItem(ANON_VISIBLE_COUNT_KEY);

      const curChecks = sessionStorage.getItem(CHECKS_KEY);
      const curSig = sessionStorage.getItem(SIGNATURE_KEY);
      const curVisible = sessionStorage.getItem(VISIBLE_COUNT_KEY);

      // 현재 사용자 키에 값이 없을 때만 anon 값을 승계
      if (anonChecks && !curChecks) {
        sessionStorage.setItem(CHECKS_KEY, anonChecks);
      }
      if (anonSig && !curSig) {
        sessionStorage.setItem(SIGNATURE_KEY, anonSig);
      }
      if (anonVisible && !curVisible) {
        sessionStorage.setItem(VISIBLE_COUNT_KEY, anonVisible);
      }

      // 마이그레이션 후 anon 값 정리
      if (anonChecks) sessionStorage.removeItem(ANON_CHECKS_KEY);
      if (anonSig) sessionStorage.removeItem(ANON_SIGNATURE_KEY);
      if (anonVisible) sessionStorage.removeItem(ANON_VISIBLE_COUNT_KEY);
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [storageNS, CHECKS_KEY, SIGNATURE_KEY, VISIBLE_COUNT_KEY, ANON_CHECKS_KEY, ANON_SIGNATURE_KEY, ANON_VISIBLE_COUNT_KEY]);

  // 체크박스 저장값 복원
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const savedChecks = sessionStorage.getItem(CHECKS_KEY);
      if (savedChecks) {
        const parsed = JSON.parse(savedChecks) as number[];
        if (Array.isArray(parsed)) {
          setMultiSelectionIdList(parsed);
        }
      }
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [CHECKS_KEY]);

  // 서명 이미지 복원 (캔버스 준비 후 1회)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!showSignature) return;
    if (restoredRef.current) return;
    if (!sigRef.current) return;
    if (canvasSize.width <= 0 || canvasSize.height <= 0) return;

    try {
      const savedSig = sessionStorage.getItem(SIGNATURE_KEY);
      if (savedSig) {
        sigRef.current.clear();
        sigRef.current.fromDataURL(savedSig);
        setSignatureDataUrl(savedSig);
        setIsSigned(true);
      }
      restoredRef.current = true;
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [showSignature, SIGNATURE_KEY, canvasSize.width, canvasSize.height, pixelRatio, canvasKey]);

  // 체크박스 변경 시 저장
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(CHECKS_KEY, JSON.stringify(multiSelectionIdList));
    } catch {
      // 무시
    }
  }, [multiSelectionIdList, CHECKS_KEY]);

  // 체크 진행도에 따라 다음 항목 노출(한번 보인 단계는 유지)
  useEffect(() => {
    const maxDisplay = options.length + 1;
    const maxCheckedId =
      multiSelectionIdList.length > 0 ? Math.max(...multiSelectionIdList) : 0;
    const desiredNext = Math.min(maxDisplay, Math.max(1, maxCheckedId + 1));

    setVisibleOptionsCount((prev) => {
      const next = Math.max(prev, desiredNext);
      if (next !== prev) {
        try {
          sessionStorage.setItem(VISIBLE_COUNT_KEY, String(next));
        } catch {
          // 무시
        }
      }
      return next;
    });
  }, [multiSelectionIdList, VISIBLE_COUNT_KEY]);

  // 서명 dataURL 변경 시 저장/삭제
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (signatureDataUrl) {
        sessionStorage.setItem(SIGNATURE_KEY, signatureDataUrl);
      } else {
        sessionStorage.removeItem(SIGNATURE_KEY);
      }
    } catch {
      // 무시
    }
  }, [signatureDataUrl, SIGNATURE_KEY]);

  const handleOptionChange = (id: number) => {
    setMultiSelectionIdList((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleClear = () => {
    setCanvasKey(Date.now()); // key를 변경하여 강제로 리마운트
    setIsSigned(false);
    setSignatureDataUrl(null);
    try {
      sessionStorage.removeItem(SIGNATURE_KEY);
    } catch {
      // 무시
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    if (!sigRef.current) return;
    const empty = sigRef.current.isEmpty();
    setIsSigned(!empty);
    if (!empty) {
      const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
      setSignatureDataUrl(dataUrl);
    }
  };

  const { mutate: requestRefund } = useMutation({
    mutationFn: (vars: { signature: string; cancelAmount: number }) =>
      refundApi.request({
        signature: vars.signature,
        cancelAmount: vars.cancelAmount,
      }),
    onSuccess: () => {
      setIsRequestRefundLoading(false);
      setOpenSubmitModal(false);

      // 성공 시 세션 저장값 정리
      try {
        sessionStorage.removeItem(SIGNATURE_KEY);
        sessionStorage.removeItem(CHECKS_KEY);
      } catch {
        // 무시
      }

      close();
    },
    onError: () => {
      setIsRequestRefundLoading(false);
    },
  });

  const handleSubmitSignature = () => {
    setOpenSubmitModal(true);
  };

  const handleConfirmRequest = () => {
    // 유효성 검사 먼저 수행 (로딩 표시 전)
    if (!sigRef.current) return;
    const empty = sigRef.current.isEmpty();
    if (!isSigned || empty) return;

    const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
    setSignatureDataUrl(dataUrl);
    if (cancelableAmount === undefined) return;

    // 요청 시작: 로딩 표시 활성화, 모달은 유지하여 로딩을 노출
    setIsRequestRefundLoading(true);
    requestRefund({ signature: dataUrl, cancelAmount: cancelableAmount });
    // 모달 닫기 및 화면 닫기는 onSuccess에서 처리합니다.
  };

  const handleConfirmCancel = () => {
    setOpenCancelModal(false);
    close();
  };

  const allAgreed = multiSelectionIdList.length === options.length;
  const cancelableAmount =
    paymentOrderDetailData?.result?.cancelableAmount ??
    paymentOrderState?.cancelableAmount;
  const submitDisabled = !allAgreed || !isSigned || cancelableAmount === undefined;

  const displayDate = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Box sx={{ paddingBottom: "120px" }}>
      <Stack
        spacing={10}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={2}>
          <CustomTypography
            variant="Subtitle"
            fontWeight={500}
            sx={{
              color: theme.vars.palette.Content.Strong,
            }}
          >
            {"아래 내용 확인 후, 환불을 도와드리겠습니다."}
          </CustomTypography>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {options
                .filter((option) => option.id <= Math.min(visibleOptionsCount, options.length))
                .map((option) => (
                  <SelectCheckbox
                    key={option.id}
                    checked={multiSelectionIdList.includes(option.id)}
                    onChange={() => handleOptionChange(option.id)}
                    label={option.text}
                    danger={true}
                  />
                ))}
            </Box>
            {showSignature && (
              <Box sx={{ mt: 2 }}>
                <Box
                  ref={signatureContainerRef}
                  sx={{
                    position: "relative",
                    borderRadius: "16px",
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    height: 200,
                    overflow: "hidden",
                  }}
                >
                  {canvasSize.width > 0 && canvasSize.height > 0 && (
                    <SignatureCanvas
                      key={canvasKey}
                      ref={sigRef}
                      onBegin={handleSignatureBegin}
                      onEnd={handleSignatureEnd}
                      penColor={"black"}
                      minWidth={1.5}
                      maxWidth={1.5}
                      canvasProps={{
                        // Use high-DPI internal resolution while keeping visual size consistent
                        width: Math.max(1, Math.floor(canvasSize.width * pixelRatio)),
                        height: Math.max(1, Math.floor(canvasSize.height * pixelRatio)),
                        style: {
                          display: "block",
                          touchAction: "none",
                          backgroundColor: "transparent",
                          width: `${canvasSize.width}px`,
                          height: `${canvasSize.height}px`,
                        },
                      }}
                    />
                  )}

                  {!isSigned && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <CustomTypography
                        variant="Body"
                        sx={{
                          color: theme.vars.palette.Content.SuperWeak,
                          fontWeight: 500,
                        }}
                      >
                        서명을 해주세요
                      </CustomTypography>
                    </Box>
                  )}

                  {isSigned && (
                    <Box sx={{ position: "absolute", right: 20, bottom: 18 }}>
                      <Typography
                        variant="Caption"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          textUnderlineOffset: "2px",
                          color: theme.vars.palette.Content.Weak,
                          fontWeight: 500,
                        }}
                        onClick={handleClear}
                      >
                        다시 쓰기
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 20,
                      bottom: 18,
                      pointerEvents: "none",
                    }}
                  >
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        color: theme.vars.palette.Content.Weak,
                        fontWeight: 500,
                      }}
                    >
                      {`${displayDate}`}
                    </CustomTypography>
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 0,
          width: "100%",
          maxWidth: "660px",
          zIndex: 1200,
          px: "20px",
          py: 2,
          display: "flex",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Button
          variant={"Outlined"}
          size={"md"}
          sx={{
            height: "54px",
            width: "50%",
            fontWeight: 700,
            backgroundColor: theme.vars.palette.Background.Level1,
          }}
          onClick={() => setOpenCancelModal(true)}
        >
          {"취소하기"}
        </Button>
        <Button
          variant={"Action"}
          color={"danger"}
          size={"md"}
          sx={{
            height: "54px",
            width: "50%",
            fontWeight: 700,
          }}
          disabled={submitDisabled}
          onClick={handleSubmitSignature}
        >
          {"제출하기"}
        </Button>
      </Box>

      {/* Submit Confirmation Modal */}
      <CustomModal
        open={openSubmitModal}
        onClose={() => setOpenSubmitModal(false)}
        title={"환불을 신청할까요?"}
        subtitle={"즉시 환불 절차가 진행되며 돌이킬 수 없어요."}
        actions={[
          {
            label: "신청하기",
            onClick: handleConfirmRequest,
            variant: "Solid",
            color: "danger",
            buttonProps: { loading: isRequestRefundLoading },
          },
        ]}
      />

      {/* Cancel Confirmation Modal */}
      <CustomModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        title={"환불 신청을 취소할까요?"}
        subtitle={"앞으로 어떤 도움이 필요한지 코치 선생님과 이야기를 나눠보세요."}
        actions={[
          {
            label: "프로그램 계속 이용하기",
            onClick: handleConfirmCancel,
            variant: "Solid",
          },
        ]}
      />
    </Box>
  );
}
